import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from 'baseui';
import { Button, SIZE } from 'baseui/button';
import { Checkbox } from 'baseui/checkbox';
import useTutorial from '../../image/labelling/providers/hooks/use-tutorials';
import { LOCK_ANNOTATION_ACTION, VISIBILITY_ANNOTATION_ACTION } from '../constant/constant';
import { TUTORIAL_DISPLAY_NAME, TUTORIAL_ERROR_MESSAGE } from '../tutorial-list/constants';
import { TUTORIAL_TYPES } from '../tutorial-list/constants';
import type { AnnotationLabelT, GenericAnnotationT, GetAnnotationMetaDetailsT, GroupedAnnotationsT } from '../types/types';
import { checkLabelMaxReached } from '../utils/utils';
import type { ToggleLockLabelPayloadT, ToggleVisibilityLabelPayloadT } from './analytics/types';
import useAnalyticsForSidePanel from './analytics/useAnalyticsForSidePanel';
import { ANNOTATION_ITEM_TUTORIAL_CONFIG, selectAnnotation } from './tutorials/annotation-items-config';
import { groupAnnotationsWithLabel, groupConsensusAnnotationsByAnnotator } from './utils/utils';
import AnnotationItem, { ANNOTATION_ITEM_ATTRIBUTE_ID, DELETE_BUTTON_SELECTOR_ATTRIBUTE_ID, SHOW_HIDE_BUTTON_SELECTOR_ATTRIBUTE_ID } from './annotation-item';
import { EmptyMessageContainer, PanelContainer } from './styled-components';
const AnnotationsContainer = styled<'div', {
  $height: number;
}>('div', ({
  $height
}) => ({
  maxHeight: `${$height === 0 ? 300 : $height}px`,
  overflow: 'auto'
}));
const SelectAllContainer = styled('div', ({
  $theme
}) => ({
  padding: `${$theme.sizing.scale200} ${$theme.sizing.scale400}`,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center'
}));
type PropsT = {
  annotationLabels: AnnotationLabelT[];
  panelHeight: number;
  editableAnnotationId: string | undefined | null;
  selectedAnnotationIds: Array<string>;
  focusedAnnotationId: string | undefined | null;
  hideAnnotations: (annotationLabel: AnnotationLabelT, hidden: boolean) => void;
  lockAnnotations?: (annotationLabel: AnnotationLabelT, lock: boolean) => void;
  handleSelectAnnotation: (annotationIdx: string) => void;
  handleFocusAnnotation: (annotationIdx: string) => void;
  handleDeleteAnnotation?: (annotationIdx: string) => void;
  handleAnnotationEdit?: (annotationIdx: string) => void;
  handleAnnotationLockToggle?: (value: boolean, annotationIdx: string) => void;
  handleAnnotationHideToggle: (value: boolean, annotationIdx: string) => void;
  annotationList: GenericAnnotationT[];
  getAnnotationMetaDetails: GetAnnotationMetaDetailsT;
  annotationImportConfig?: {
    onImportAnnotations: (annotationIdList: string[]) => void;
    importedAnnotationIds: {
      [x: string]: boolean;
    };
  };
  handleDeleteAnnotations: (annotationLabel: string) => void;
  handleCopyAnnotationId?: (annotationId: string | null) => void;
  isConsensusPanel?: boolean;
};
const AnnotationsPane = ({
  annotationLabels,
  panelHeight,
  editableAnnotationId,
  selectedAnnotationIds,
  focusedAnnotationId,
  hideAnnotations,
  lockAnnotations,
  handleSelectAnnotation,
  handleFocusAnnotation,
  handleDeleteAnnotation,
  handleCopyAnnotationId,
  handleAnnotationLockToggle,
  handleAnnotationHideToggle,
  handleAnnotationEdit,
  annotationList,
  getAnnotationMetaDetails,
  annotationImportConfig,
  handleDeleteAnnotations,
  isConsensusPanel = false
}: PropsT) => {
  const [checkedAnnotationIds, setCheckedAnnotationIds] = useState({});
  const availableAnnotationIds = useMemo(() => {
    if (!annotationImportConfig) return [];
    return annotationList.map(annotation => annotation.id).filter(id => !annotationImportConfig.importedAnnotationIds[id]);
  }, [annotationList, annotationImportConfig]);
  const {
    isAllChecked,
    isIndeterminate
  } = useMemo(() => {
    if (!annotationImportConfig || availableAnnotationIds.length === 0) {
      return {
        isAllChecked: false,
        isIndeterminate: false
      };
    }
    const checkedCount = Object.keys(checkedAnnotationIds).length;
    return {
      isAllChecked: checkedCount === availableAnnotationIds.length && checkedCount > 0,
      isIndeterminate: checkedCount > 0 && checkedCount < availableAnnotationIds.length
    };
  }, [availableAnnotationIds, checkedAnnotationIds, annotationImportConfig]);
  const handleSelectAllChange = () => {
    if (isAllChecked) {
      setCheckedAnnotationIds({});
    } else {
      const allAnnotationsChecked = availableAnnotationIds.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {});
      setCheckedAnnotationIds(allAnnotationsChecked);
    }
  };
  const showAsCheckedAnnotations = useMemo(() => {
    if (!annotationImportConfig) {
      return checkedAnnotationIds;
    }
    return Object.keys(annotationImportConfig.importedAnnotationIds).reduce((ids, annotationId: string) => {
      ids[annotationId] = annotationImportConfig.importedAnnotationIds[annotationId];
      return ids;
    }, {
      ...checkedAnnotationIds
    });
  }, [checkedAnnotationIds, annotationImportConfig]);
  const {
    sendEventForToggleVisibilityLabel,
    sendEventForToggleLockLabel
  } = useAnalyticsForSidePanel();
  const handleSelectGroupedAnnotations = useCallback((annotations: GenericAnnotationT[], isSelected: boolean) => {
    if (isSelected) {
      const annotationIds = annotations.reduce((acc, annotation) => {
        acc[annotation.id] = true;
        return acc;
      }, {});
      setCheckedAnnotationIds(prev => ({
        ...prev,
        ...annotationIds
      }));
    } else {
      setCheckedAnnotationIds(prev => {
        const updated = {
          ...prev
        };
        annotations.forEach(annotation => {
          delete updated[annotation.id];
        });
        return updated;
      });
    }
  }, []);
  const addedAnnotationByLabel: GroupedAnnotationsT[] = isConsensusPanel ? groupConsensusAnnotationsByAnnotator(annotationList) : groupAnnotationsWithLabel(annotationList);
  const checkUpdateAnnotationLabel = (annotationLabel: AnnotationLabelT) => {
    return !checkLabelMaxReached(annotationLabel, annotationList);
  };
  const handleToggleAnnotationLock = (label: AnnotationLabelT, lock: boolean) => {
    if (!lockAnnotations) {
      return null;
    }
    lockAnnotations(label, lock);
    let eventPayload: ToggleLockLabelPayloadT = {
      label: label.title,
      action: lock ? LOCK_ANNOTATION_ACTION.LOCK : LOCK_ANNOTATION_ACTION.UNLOCK
    };
    sendEventForToggleLockLabel(eventPayload);
  };
  const handleToggleAnnotationVisibility = (label: AnnotationLabelT, hidden: boolean) => {
    hideAnnotations(label, hidden);
    let eventPayload: ToggleVisibilityLabelPayloadT = {
      label: label.title,
      action: hidden ? VISIBILITY_ANNOTATION_ACTION.HIDE : VISIBILITY_ANNOTATION_ACTION.SHOW
    };
    sendEventForToggleVisibilityLabel(eventPayload);
  };
  const handleAnnotationCheckedToggle = annotationImportConfig && ((annotationId: string, isChecked: boolean) => {
    if (isChecked) {
      setCheckedAnnotationIds(annotationIds => ({
        ...annotationIds,
        [annotationId]: true
      }));
      return;
    }
    setCheckedAnnotationIds(annotationIds => {
      const ids = {
        ...annotationIds
      };
      delete ids[annotationId];
      return ids;
    });
  });
  const onAnnotationsImport = annotationImportConfig && (() => {
    const checkedAnnotationIdList = Object.keys(checkedAnnotationIds);
    checkedAnnotationIdList.length > 0 && annotationImportConfig.onImportAnnotations(checkedAnnotationIdList);
    setCheckedAnnotationIds({});
  });
  const {
    addStep,
    onTutorialReplay
  } = useTutorial();
  useEffect(() => {
    const cleanUp = onTutorialReplay(selectAnnotation.id, TUTORIAL_TYPES.SIDE_PANEL, TUTORIAL_DISPLAY_NAME.annotation, () => {
      addStep({
        ...ANNOTATION_ITEM_TUTORIAL_CONFIG.selectAnnotation,
        getTarget: () =>
        // @ts-expect-error todo(ts-migration) TS2322 Type 'Element | undefined' is not assignable to type 'Element | null'.
        document.querySelectorAll(`[data-selector-id=${ANNOTATION_ITEM_ATTRIBUTE_ID}]`)[0],
        isReplay: true
      }, selectAnnotation.id);
      addStep({
        ...ANNOTATION_ITEM_TUTORIAL_CONFIG.hideAnnotation,
        getTarget: () =>
        // @ts-expect-error todo(ts-migration) TS2322 Type 'Element | undefined' is not assignable to type 'Element | null'.
        document.querySelectorAll(`[data-selector-id=${SHOW_HIDE_BUTTON_SELECTOR_ATTRIBUTE_ID}]`)[0]
      }, selectAnnotation.id);
      addStep({
        ...ANNOTATION_ITEM_TUTORIAL_CONFIG.deleteAnnotation,
        getTarget: () =>
        // @ts-expect-error todo(ts-migration) TS2322 Type 'Element | undefined' is not assignable to type 'Element | null'.
        document.querySelectorAll(`[data-selector-id=${DELETE_BUTTON_SELECTOR_ATTRIBUTE_ID}]`)[0]
      }, selectAnnotation.id);
    }, () => annotationList.length > 0 ? {
      state: true
    } : {
      state: false,
      reason: TUTORIAL_ERROR_MESSAGE.annotation
    });
    return cleanUp;
  }, [addStep, annotationList.length, onTutorialReplay]);
  return <PanelContainer>
      {annotationImportConfig && availableAnnotationIds.length > 0 && <SelectAllContainer>
          <Checkbox checked={isAllChecked} isIndeterminate={isIndeterminate} onChange={handleSelectAllChange} overrides={{
        Root: {
          style: {
            marginBottom: 0
          }
        },
        Label: {
          style: ({
            $theme
          }) => ({
            fontSize: $theme.typography.LabelSmall.fontSize
          })
        }
      }}>
            Select All
          </Checkbox>
        </SelectAllContainer>}
      <AnnotationsContainer $height={panelHeight}>
        {addedAnnotationByLabel.length === 0 && <EmptyMessageContainer>No annotations available</EmptyMessageContainer>}
        {addedAnnotationByLabel.map(labelData => <AnnotationItem annotationLabels={annotationLabels} data={{
        ...labelData.label,
        annotations: labelData.annotationList
      }} key={labelData.label.title} editableAnnotationId={editableAnnotationId} selectedAnnotationIds={selectedAnnotationIds} focusedAnnotationId={focusedAnnotationId} onAnnotationEdit={handleAnnotationEdit} onLockClick={() => handleToggleAnnotationLock(labelData.label, false)} onUnlockClick={() => handleToggleAnnotationLock(labelData.label, true)} onHideClick={() => handleToggleAnnotationVisibility(labelData.label, true)} onShowClick={() => handleToggleAnnotationVisibility(labelData.label, false)} onAnnotationSelect={handleSelectAnnotation} onAnnotationHover={handleFocusAnnotation} onAnnotationDelete={handleDeleteAnnotation} onCopyAnnotationId={handleCopyAnnotationId} onLabelAnnotationsDelete={handleDeleteAnnotations} onAnnotationHideToggle={handleAnnotationHideToggle} checkUpdateAnnotationLabel={checkUpdateAnnotationLabel} getAnnotationMetaDetails={getAnnotationMetaDetails} onAnnotationCheckedToggle={handleAnnotationCheckedToggle} checkboxDisabledIds={annotationImportConfig?.importedAnnotationIds} checkedIds={showAsCheckedAnnotations} {...handleAnnotationLockToggle && !isConsensusPanel && {
        onAnnotationLockToggle: handleAnnotationLockToggle
      }} {...isConsensusPanel && {
        isConsensusPanel,
        handleSelectGroupedAnnotations,
        isGroupSelected: labelData.annotationList.every(annotation => checkedAnnotationIds[annotation.id])
      }} />)}
      </AnnotationsContainer>
      {onAnnotationsImport && <Button size={SIZE.mini}
    // @ts-expect-error todo(ts-migration) TS2322 Type '{ children: string; size: "mini"; $style: () => { width: string; margin: string; alignSelf: string; }; onClick: () => void; disabled: boolean; }' is not assignable to type 'IntrinsicAttributes &...
    $style={() => ({
      width: '90%',
      margin: '5px',
      alignSelf: 'center'
    })} onClick={() => {
      onAnnotationsImport();
    }} disabled={Object.keys(checkedAnnotationIds).length === 0}>
          Import
        </Button>}
    </PanelContainer>;
};
export default AnnotationsPane;